import {
  Card,
  CardContent,
  CardHeader,
  H2,
  H5,
  H6,
} from "@sellar-io/components";
import {
  FulfillmentTypeListResponseType,
  OrderGetResponseType,
  StockListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  findProductFulfillmentTypeDeliveryCharge,
  formatCurrency,
  isIneligibleForFulfillmentType,
} from "@sellar-io/common";
import { useMatch } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { StockIndicator } from "../../../components";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../components/skeleton/Skeleton";
import { STOREFRONT_LINKS_PREVIEW } from "../../../constants/routes";
import { ContextOrderBasketType, useSellarUser } from "../../../hooks";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSignupContext } from "../../../hooks/useSignupContext.shared";
import { generateSignupContinueUrl } from "../../../utils/sellarHelpers";
import { DiscountedPrice } from "../DiscountedPrice";
import { getProductStockToDisplay } from "../helpers";
import { ItemQuantity } from "./ItemQuantity";
import { PriceCalculator } from "./PriceCalculator/PriceCalculator";
import { MaxOrderQuantityBadge } from "../../../components/badge/MaxOrderQuantityBadge";
import { AvailableFromBadge } from "../../../components/badge/AvailableFromBadge";
import { SignUpToOrderTooltip } from "./Storefront/SignUpToOrderTooltip/SignUpToOrderTooltip";
import { CheckoutSelectedFulfillmentType } from "..";
import { DietaryDisplayList } from "../../../components/dietaryDisplayList/DietaryDisplayList";
import { buildDietaryList } from "../../../components/dietaryDisplayList/buildDietaryList";

export default function Item({
  item,
  supplier,
  submittedOrder,
  supplierStorefront,
  basketOrder,
  basketTotal,
  selectedFulfillmentType,
  courierFulfillmentType,
}: {
  item: StockListResponseType[number]["Products"][number];
  basketOrder?: ContextOrderBasketType | null;
  submittedOrder?: OrderGetResponseType;
  supplier: SupplierGetResponseType | undefined;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
  basketTotal?: number;
  selectedFulfillmentType: CheckoutSelectedFulfillmentType;
  courierFulfillmentType: FulfillmentTypeListResponseType[number] | undefined;
}) {
  const { setSignupDialogActive, setBasketTotal } = useSignupContext();

  const unbundlingEnabled =
    (supplierStorefront?.deliveryMethods?.length ?? 0) > 0;

  const palletOnly =
    !unbundlingEnabled &&
    isIneligibleForFulfillmentType({
      item,
      fulfillmentType: courierFulfillmentType,
    }) &&
    selectedFulfillmentType === "courier";

  const [, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );
  const [searchParams] = useSearchParams();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();

  const isLoggedIn = !!business && !!user;
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const isStorefrontLinkPreview = !!useMatch(STOREFRONT_LINKS_PREVIEW);
  const deliveryCharge =
    findProductFulfillmentTypeDeliveryCharge({
      fulfillmentTypeContainerDeliveryCharges:
        supplierStorefront?.FulfillmentType
          .FulfillmentTypeContainerDeliveryCharges,
      product: {
        ...item,
        displayUnit: item.unit,
      },
    })?.charge ?? 0;

  if (
    item.ProductAvailabilityTier &&
    item.ProductAvailabilityTier.active === false
  )
    return null;

  const canShowPricing = (business || storefrontLink) && item.ProductPriceTier;

  const dietaryList = buildDietaryList({
    childProduct: item,
    parentProduct: {
      vegan: item.Parent?.vegan,
      lactoseFree: item.Parent?.lactoseFree,
      glutenFree: item.Parent?.glutenFree,
    },
  });

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge(
          "bg-white",
          "p-4",
          searchParams.get("categoryId") && "rounded-t border",
        )}
      >
        <div className="flex flex-col gap-2">
          <div className={"flex flex-wrap items-center justify-between gap-2"}>
            <div className="flex flex-col">
              <H2> {item.name?.split(" • ").slice(1).join(" • ")}</H2>
              {item.ProductPriceTier?.discountable === false && (
                <H6>This item is not eligible for volume discounts.</H6>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {item.type !== "badge" && (
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: dietaryList.includes("VG"),
                  LF: dietaryList.includes("LF"),
                  GF: dietaryList.includes("GF"),
                }}
                mode="badge"
              />
            )}
            <StockIndicator
              stock={getProductStockToDisplay(
                { id: item.id, availableStock: item.availableStock },
                basketOrder,
                submittedOrder,
              )}
              showStock={supplier?.showStock}
              canBackorder={item.canBackorder}
            />
            {!!item.maxOrderQuantity && (
              <MaxOrderQuantityBadge maxQuantity={item.maxOrderQuantity} />
            )}
          </div>
          <div className="flex gap-1">
            {!!item.availableFrom && (
              <AvailableFromBadge availableFrom={item.availableFrom} />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent
        className={twMerge(
          "p-4",
          searchParams.get("categoryId") && "rounded-b border",
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2 md:flex-row">
            {canShowPricing ? (
              <>
                <div className="grid grid-cols-6">
                  <div className="col-span-4">
                    <H5>Unit price (ex. VAT)</H5>
                  </div>
                  <div className="col-span-2">
                    {item.ProductPriceTier && (
                      <H5 className="select-text">
                        {(isLoggedIn || storefrontLink) &&
                          item.ProductPriceTier.price >= 0 && (
                            <DiscountedPrice
                              productPriceTier={item.ProductPriceTier}
                            />
                          )}
                      </H5>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {!!deliveryCharge && (
                    <>
                      <div className="col-span-4">
                        <H6>Item delivery charge</H6>
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <H6 className={"flex place-content-end items-center"}>
                          {" "}
                          {palletOnly
                            ? "Pallet only"
                            : formatCurrency(
                                deliveryCharge,
                                supplier?.currency,
                              )}
                        </H6>
                      </div>
                    </>
                  )}
                </div>
                {((business?.Retailer &&
                  (supplierStorefront?.SupplierRetailerConnection ||
                    !!supplierStorefront)) ||
                  storefrontLink) && (
                  <>
                    {isDesktop && (
                      <div className="">
                        <ItemQuantity
                          productItem={item}
                          showMaxQuantity={true}
                          selectedFulfillmentType={selectedFulfillmentType}
                        />
                      </div>
                    )}
                    {!isDesktop && (
                      <div className="m-auto">
                        <div className="">
                          <ItemQuantity
                            productItem={item}
                            showMaxQuantity={true}
                            selectedFulfillmentType={selectedFulfillmentType}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {(isLoggedIn || isStorefrontLinkPreview) && (
                  <div className="grid w-full grid-cols-6">
                    <div className="col-span-3">
                      <H5>Unit price (ex. VAT)</H5>
                    </div>
                    <div className="col-span-3 flex justify-end">
                      {isStorefrontLinkPreview ? (
                        <SkeletonContainer>
                          <SkeletonText className={"h-[20px] w-[75px]"} />
                        </SkeletonContainer>
                      ) : (
                        <H5>£ Pricing on request</H5>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            {!isLoggedIn && !canShowPricing && (
              <>
                <H5>Unit price (ex. VAT)</H5>
                <SkeletonContainer
                  className={
                    "flex w-[150px] animate-none cursor-pointer items-center justify-center"
                  }
                  onClick={() => {
                    const continueSearchParams = generateSignupContinueUrl(
                      supplier?.id,
                      storefrontLink ? `storefrontLink=${storefrontLink}` : "",
                    );
                    setOnboardingContinueUrl(
                      `${continueSearchParams.get("continue")}${
                        continueSearchParams.get("queryParameters")
                          ? `?${continueSearchParams.get("queryParameters")}`
                          : ""
                      }`,
                    );

                    setBasketTotal(basketTotal);
                    setSignupDialogActive(true);
                  }}
                >
                  <SignUpToOrderTooltip enable={true}>
                    <div className="flex items-center gap-1">
                      £
                      <SkeletonText
                        className={"h-6 w-full animate-pulse bg-zinc-100"}
                      />
                    </div>
                  </SignUpToOrderTooltip>
                </SkeletonContainer>
              </>
            )}
          </div>
          {isLoggedIn &&
            item.ProductPriceTier &&
            item.ProductPriceTier.price >= 0 &&
            (item.containerType?.toLowerCase().includes("keg") ||
              item.containerType?.toLowerCase().includes("cask") ||
              item.containerType === "flatBottomPin" ||
              item.containerType === "polyPin" ||
              item.containerType === "bottle" ||
              item.containerType === "can") && (
              <PriceCalculator product={item} supplier={supplier} />
            )}
        </div>
      </CardContent>
    </Card>
  );
}
